import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import analytics from '@repo/analytics';
import teamsApi from '@repo/editor/src/api/teams';
import DecisionModal from '@repo/editor/src/components/DecisionModal/DecisionModal';
import {
  SubscriptionModal,
  TeamSizeModal,
  SubscriptionOffboardingModal,
} from '@repo/editor/src/components/Subscription';
import { ENABLE_BILLING } from '@repo/editor/src/global/constants';
import { useShowTwoPlansForFreeUser } from '@repo/editor/src/global/growthbookUtils';
import useFreeTrial from '@repo/editor/src/hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@repo/editor/src/stores/planSelectionStore';
import {
  SUCCESS_UPGRADE_PAYMENT_URL_PARAM,
  SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE,
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { Badge } from '@repo/ui/components/Badge';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { fireToast } from '@repo/ui/components/Toast';
import { H4, P2, P4 } from '@repo/ui/components/Typography';
import { format } from 'date-fns';
import { capitalize } from 'lodash';

import { YOUR_SUBSCRIPTION_CLICK } from 'global/analyticsEvents';
import { FlexRow, FlexColumn } from 'styles/layouts';

import { ChangeTeamSizeLink, Skeleton, Wrapper } from './styles';
import { SubscriptionCardProps } from './types';
import { getCardTextTeam } from './util';

const cardTextsUser = {
  [SubscriptionPlanType.FREE]:
    'Drag & drop editor to create stunning designs with just a few clicks. Use the template and elements library to work on a limited number of projects. Download JPG & PNG files with up to 800px width.',
  [SubscriptionPlanType.PRO]:
    'Use the complete template and elements library and create multiple projects. Download scalable PDF & SVG vector files and high res images. Use 10GB upload space and upload your own fonts.',
  [SubscriptionPlanType.EXPERT]:
    'Use the complete template and elements library and create unlimited projects. Download scalable PDF & SVG vector files and high res images. Use 100GB upload space and upload your own fonts.',
};

const SkeletonParagraphs = (): React.ReactElement => {
  return (
    <>
      <Skeleton>&nbsp;</Skeleton>
      <Skeleton>&nbsp;</Skeleton>
    </>
  );
};

const enum Modals {
  RequestUpgrade,
  Subscription,
  ChangeTeamSize,
  Offboarding,
}

/**
 * This component displays the subscription card with details about the user's current subscription plan.
 * It handles various actions such as upgrading the plan, viewing billing information, and changing the team size based on the user's subscription status.
 */
const SubscriptionCard: React.FC<SubscriptionCardProps> = (props) => {
  const showTwoPlansForFreeUsers = useShowTwoPlansForFreeUser();

  const { reset: resetPlanSelection } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const searchParams = useSearchParams();
  const openBillingPortal = useSubscriptionStore(
    subscriptionStoreSelector.openBillingPortal
  );
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const [showModal, setShowModal] = useState<Modals | null>(null);
  const [isLoadingBillingPortal, setIsLoadingBillingPortal] = useState(false);
  const plan = props.plan || 'FREE';
  const user = useUserStore(userStoreSelector.user);
  const isTeam = user?.isTeam;
  const isTeamAdmin = user?.team?.role === 'ADMIN';
  const upgradeFromFreeLabel = showTwoPlansForFreeUsers
    ? 'Upgrade Plan'
    : 'Get Pro';

  const getShowOnlyOnePlan = () => {
    // if showTwoPlansForFreeUsers is enabled and user is on free plan, show two plans
    return !(showTwoPlansForFreeUsers && plan === 'FREE');
  };

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  const freeTrial = useFreeTrial();

  const isInitialized = useSubscriptionStore(
    subscriptionStoreSelector.isInitialized
  );
  // Handle showing the subscription modal based on the user's team status and free trial availability
  const handleShowSubscriptionModal = useCallback(() => {
    if (isTeam && !isTeamAdmin) {
      setShowModal(Modals.RequestUpgrade);
    } else if (freeTrial.isAvailable) {
      showUpgradeModal('freeTrial');
    } else {
      setShowModal(Modals.Subscription);
    }
  }, [isTeam, isTeamAdmin, freeTrial.isAvailable, showUpgradeModal]);

  // ref is used to not show modal again on every subscription change
  const hasSeenModalBasedOnQuery = useRef(false);
  useEffect(() => {
    if (
      !hasSeenModalBasedOnQuery.current &&
      searchParams?.get('change') &&
      !freeTrial.isPaidByKittl
    ) {
      hasSeenModalBasedOnQuery.current = true;
      handleShowSubscriptionModal();
    }
  }, [freeTrial.isPaidByKittl, handleShowSubscriptionModal, searchParams]);

  const { setFromSubscription } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  if (!user) {
    return null;
  }

  if (!isInitialized) {
    return null;
  }

  // Handle the opening of the billing portal and event tracking
  const handlePortal = async (label: string): Promise<void> => {
    setIsLoadingBillingPortal(true);
    await openBillingPortal();
    void analytics.track(YOUR_SUBSCRIPTION_CLICK, { label });
    setIsLoadingBillingPortal(false);
  };

  // Handle upgrading from the free plan
  const handleUpgradeFromFree = async (): Promise<void> => {
    const label = showTwoPlansForFreeUsers
      ? 'Upgrade from Free Plan'
      : 'Get Pro';
    void analytics.track(YOUR_SUBSCRIPTION_CLICK, {
      label,
    });
    resetPlanSelection();
    return await handleShowSubscriptionModal();
  };

  const handleChangePlanClick = (): void => {
    if (currentSubscription) {
      setFromSubscription(currentSubscription);
    }

    handleShowSubscriptionModal();
  };

  let buttons = null;
  const linkDisabled = !ENABLE_BILLING || props.disabled;

  const viewBillingInformationButton = (
    <Button
      disabled={linkDisabled || isLoadingBillingPortal}
      height="40px"
      label={isLoadingBillingPortal ? 'Loading...' : 'View Billing Information'}
      name="viewBillingInformationButton"
      width="200px"
      onClick={(): void => {
        void handlePortal('View Billing Information');
        void openBillingPortal();
      }}
    />
  );

  // Define the buttons based on the current plan and free trial status
  if (plan === 'FREE') {
    buttons = (
      <FlexColumn alignItems="center" gap="4px">
        <Button
          disabled={!ENABLE_BILLING || props.disabled}
          height="40px"
          label={
            freeTrial.isAvailable ? 'Try Pro for Free' : upgradeFromFreeLabel
          }
          name="upgradeFromFreeButton"
          width="200px"
          onClick={handleUpgradeFromFree}
        />
        {freeTrial.isCancelled && freeTrial.isRunning && (
          <P4>
            Your trial has been canceled and will end on{' '}
            {format(freeTrial.trialEndDate, 'MMM d, yyyy')}.
          </P4>
        )}
      </FlexColumn>
    );
  } else if (freeTrial.isRunning && !freeTrial.isCancelled) {
    buttons = (
      <>
        {viewBillingInformationButton}
        <FlexColumn alignItems="center" gap="4px">
          <Button
            disabled={linkDisabled}
            height="40px"
            label="Cancel Trial"
            name="cancelTrialButton"
            theme={secondaryTheme}
            width="200px"
            onClick={(): void => setShowModal(Modals.Offboarding)}
          />
          <P4>
            Trial ends on {format(freeTrial.trialEndDate, 'MMM d, yyyy')}.
          </P4>
        </FlexColumn>
      </>
    );
  } else {
    buttons = (
      <>
        {viewBillingInformationButton}
        <Button
          disabled={linkDisabled}
          height="40px"
          label="Change Plan"
          name="subscriptionChangePlanButton"
          theme={secondaryTheme}
          width="200px"
          onClick={handleChangePlanClick}
        />
      </>
    );
  }

  return (
    <Wrapper>
      <FlexColumn gap="4px">
        <FlexRow alignItems="center" gap="8px">
          <Badge
            size="s"
            text={freeTrial.isRunning ? 'TRIAL' : plan}
            variant="blue"
          />

          <H4>
            {capitalize(plan)} plan{' '}
            {freeTrial.isRunning ? 'trial' : 'subscription'}
            {user.isTeam ? ' (Team)' : ''}
          </H4>
        </FlexRow>
        <Spacer height="24px" />
        <P2>
          {user.isTeam &&
            (currentSubscription ? (
              getCardTextTeam(currentSubscription)
            ) : (
              <SkeletonParagraphs />
            ))}
          {!user.isTeam && cardTextsUser[plan]}
        </P2>
        {user.isTeam && currentSubscription?.plan && (
          <div>
            <ChangeTeamSizeLink
              onClick={(): void => setShowModal(Modals.ChangeTeamSize)}
            >
              Change team size
            </ChangeTeamSizeLink>
          </div>
        )}
      </FlexColumn>
      <FlexColumn alignItems="center" gap="12px" justifyContent="end">
        {buttons}
      </FlexColumn>
      <DecisionModal
        firstButton={{
          label: 'Request Upgrade',
          type: 'primary',
          width: 'auto',
          onClick: async (): Promise<void> => {
            const response = await teamsApi.requestUpgrade();
            if (!response.success) {
              fireToast({
                label:
                  'Failed to send a request to upgrade your plan. Please try again.',
                error: true,
                duration: 5000,
              });
            } else {
              fireToast({
                label:
                  'Sent a request to upgrade your plan to your team admin.',
                duration: 5000,
              });
              setShowModal(null);
            }
          },
        }}
        isOpen={showModal === Modals.RequestUpgrade}
        message={
          <>
            You don&apos;t have permission to edit team subscriptions. Only your
            team admin can upgrade your account.
          </>
        }
        title="Upgrade Plan"
        width="400px"
        onClose={(): void => setShowModal(null)}
      />
      <SubscriptionModal
        isOpen={showModal === Modals.Subscription}
        returnUrlParams={{
          [SUCCESS_UPGRADE_PAYMENT_URL_PARAM]:
            SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE,
        }}
        shouldShowOnlyOnePlan={getShowOnlyOnePlan()}
        onClose={(): void => setShowModal(null)}
      />
      <TeamSizeModal
        isOpen={showModal === Modals.ChangeTeamSize}
        onClose={(): void => setShowModal(null)}
      />
      {currentSubscription && (
        <SubscriptionOffboardingModal
          isOpen={showModal === Modals.Offboarding}
          subscription={currentSubscription}
          user={user}
          onClose={(): void => setShowModal(null)}
        />
      )}
    </Wrapper>
  );
};

export default withEventScope(SubscriptionCard, { module: 'subscriptionCard' });
